import type { CombinedError } from '@urql/core';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { TAXONOMY_TREE_QUERY, TaxonomyType } from '@revelio/data-access';

import type { TreeData } from '../../components/tree/useTreeApi';
import {
  getTaxonomyBranchLabels,
  getTaxonomyCacheKey,
  normalizeTaxonomyTree,
  type TaxonomyLevelMetadata,
} from '../utils/taxonomyTree';

export type UseTaxonomyTreeResult = {
  cacheKey: string | null;
  treeData: TreeData[];
  levels: TaxonomyLevelMetadata[];
  branchLabels: ReturnType<typeof getTaxonomyBranchLabels>;
  fetching: boolean;
  error: CombinedError | undefined;
};

export const useTaxonomyTree = ({
  taxonomyType,
  taxonomyId,
  taxonomyVersion,
  levels,
  pause = false,
}: {
  taxonomyType: TaxonomyType;
  taxonomyId?: number;
  taxonomyVersion?: string;
  levels?: number[];
  pause?: boolean;
}): UseTaxonomyTreeResult => {
  const levelsKey = levels ? [...levels].sort((a, b) => a - b).join(',') : '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on sorted value key so callers may pass inline arrays safely
  const normalizedLevels = useMemo(
    () => (levels ? [...levels].sort((a, b) => a - b) : undefined),
    [levelsKey]
  );
  const includeTitles =
    taxonomyType === TaxonomyType.Role || taxonomyType === TaxonomyType.Skill;
  const variables = useMemo(
    () => ({
      taxonomyType,
      taxonomyId: taxonomyId ?? 0,
      taxonomyVersion,
      levels: normalizedLevels,
      includeTitles,
    }),
    [includeTitles, normalizedLevels, taxonomyId, taxonomyType, taxonomyVersion]
  );
  const context = useMemo(
    () => ({
      priority: 'low' as const,
      skipGlobalErrorHandling: true,
    }),
    []
  );
  const cacheKey =
    taxonomyId === undefined
      ? null
      : getTaxonomyCacheKey({
          taxonomyType,
          taxonomyId,
          taxonomyVersion,
          levels: normalizedLevels,
        });
  const [{ data, fetching, error }] = useQuery({
    query: TAXONOMY_TREE_QUERY,
    variables,
    pause: pause || taxonomyId === undefined || cacheKey === null,
    requestPolicy: 'cache-first',
    context,
  });

  const taxonomyPayload = data?.taxonomy;
  const normalizedTree = useMemo(
    () => normalizeTaxonomyTree(taxonomyPayload),
    [taxonomyPayload]
  );

  const branchLabels = useMemo(
    () => getTaxonomyBranchLabels(normalizedTree.levels),
    [normalizedTree.levels]
  );

  return {
    cacheKey,
    treeData: normalizedTree.nodes,
    levels: normalizedTree.levels,
    branchLabels,
    fetching,
    error,
  };
};
